import {
  Color,
  FrontSide,
  LessEqualDepth,
  type Material,
  Mesh,
  type Object3D,
} from 'three';

const PLACEHOLDER_TRANSITION_GHOST_OPACITY = 0.5;
const PLACEHOLDER_TRANSITION_GHOST_TINT_AMOUNT = 0.5;
const PLACEHOLDER_TRANSITION_GHOST_NEUTRAL = new Color('#808080');

type GhostMaterial = Material & {
  color?: Color;
  transparent?: boolean;
  opacity?: number;
};

export type PlaceholderTransitionGhostRestoreEntry = {
  mesh: Mesh;
  material: Material | Material[];
};

export function getPlaceholderTransitionGhostOpacity() {
  return PLACEHOLDER_TRANSITION_GHOST_OPACITY;
}

export function applyPlaceholderTransitionGhostMaterials(
  scene: Object3D,
): PlaceholderTransitionGhostRestoreEntry[] {
  const restoreEntries: PlaceholderTransitionGhostRestoreEntry[] = [];

  scene.traverse((object) => {
    if (!(object instanceof Mesh)) {
      return;
    }

    const sourceMaterial = object.material;
    restoreEntries.push({
      mesh: object,
      material: sourceMaterial,
    });

    const sourceMaterials = Array.isArray(sourceMaterial)
      ? sourceMaterial
      : [sourceMaterial];
    const ghostMaterials = sourceMaterials.map((material) => {
      const ghostMaterial = material.clone() as GhostMaterial;

      ghostMaterial.side = FrontSide;
      ghostMaterial.transparent = true;
      ghostMaterial.opacity = PLACEHOLDER_TRANSITION_GHOST_OPACITY;
      ghostMaterial.depthTest = true;
      ghostMaterial.depthFunc = LessEqualDepth;
      ghostMaterial.depthWrite = false;

      if (ghostMaterial.color instanceof Color) {
        ghostMaterial.color.lerp(
          PLACEHOLDER_TRANSITION_GHOST_NEUTRAL,
          PLACEHOLDER_TRANSITION_GHOST_TINT_AMOUNT,
        );
      }

      ghostMaterial.needsUpdate = true;
      return ghostMaterial;
    });

    object.material = Array.isArray(sourceMaterial)
      ? ghostMaterials
      : ghostMaterials[0]!;
  });

  return restoreEntries;
}

export function restorePlaceholderTransitionGhostMaterials(
  restoreEntries: readonly PlaceholderTransitionGhostRestoreEntry[],
) {
  for (const entry of restoreEntries) {
    entry.mesh.material = entry.material;
  }
}
